<template>
  <div class="customize-panel" name="slide-customise" v-aha-emit-action.view>
    <div class="flex flex-col gap-2 relative">
      <div>
        <div class="flex justify-between items-center">
          <div class="flex font-bold">
            {{ $i18nPresenter.t('Background image') }}
            <span class="relative">
              <img
                class="cursor-pointer ml-2 mb-0.5"
                src="@/assets/images/pro.svg"
                v-if="isPlanFree && isBeforeCurrentPeriodStart"
                @click="openUpgradeBackground()"
              />
              <div v-if="ctaUpgradeBackground && isBeforeCurrentPeriodStart" class="upgrade-arrow left-2"></div>
            </span>
          </div>
          <div v-if="!thumbnailBackgroundImage">
            <aha-antd-button variant="secondary" :class="'flex items-center'" @click="openModalUploadImage">
              <aha-icon
                name="plus-new"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                class="mr-2"
              /> {{ $i18nPresenter.t('Add') }}
            </aha-antd-button>
          </div>
        </div>
        <PlanUpgradeModal
          v-if="isBeforeCurrentPeriodStart"
          :plan-for-feature="'Essential'"
          :isShowTooltip="ctaUpgradeBackground"
          :name-feature="'Background Image'"
          @outsideEvent="clickOutsideUpgradeBackground"
          top="50px"
        />
      </div>
      <aha-background-image
        :thumbnailBackgroundImage="thumbnailBackgroundImage"
        :deleteImage="deleteImage"
        :changeImage="openModalUploadImage"
        :editImage="handleRepositionButtonClick"
        :textChangeImage="$i18nPresenter.t('Change image')"
        :textEditImage="$i18nPresenter.t('Edit image')"
        v-if="thumbnailBackgroundImage"
      />
    </div>
    <div class="flex flex-col gap-2" v-if="thumbnailBackgroundImage">
      <div class="font-bold">
        {{ $i18nPresenter.t('Transparency') }}
      </div>
      <aha-antd-slider-with-input
        class="h-10"
        :min="0"
        :max="100"
        :value="getSlideVisibility"
        :change="updateSelectSlideVisibility"
        suffix="%"
      />
    </div>
    <div class="divider bg-secondary-20 h-px"></div>
    <div class="flex justify-between items-center">
      <div class="font-bold">
        {{ $i18nPresenter.t('Background colour') }}
      </div>
      <ColorPickerWrap
        :color="selectedSlide.baseColour"
        :disableUpdateBaseColor="disableUpdateBaseColor"
      />
    </div>
    <div class="divider bg-secondary-20 h-px"></div>
    <div class="flex justify-between items-center">
      <div class="font-bold w-9/12">
        {{ $i18nPresenter.t('Text') }}
        <UpgradeIcon
          ref="iconNeedUpgradePlanComponentFonts"
          :noPermission="planBenefit.brandingCustomisation &&
            !planBenefit.brandingCustomisation.premiumFontsLibrary"
          :defaultTooltip="$i18nPresenter.t('Changing fonts here affects all slides')"
          left="-40px"
          :upgradePlan="planBenefit.brandingCustomisation.premiumFontsLibrary_planName"
          featureName="Custom Fonts"
          v-if="planBenefit.interactivePresentation"
        />
      </div>
      <div class="flex items-center justify-between gap-2">
        <SelectFontWrap
          :fontFamily="$store.state.presentation.fontFamily"
          @setFontFamily="handleSetFontFamily"
          @testFontFamily="testFontFamily"
          @triggerTooltipNeedUpdatePlan="triggerTooltipNeedUpdatePlan"
        />
        <div class="flex items-center gap-2">
          <ColorButton
            :color="'#ffffff'"
            :active="isWhiteText"
            @color-selected="previewSelectTextColour"
          />
          <ColorButton
            :color="'#313131'"
            :active="isBlackText"
            @color-selected="previewSelectTextColour"
          />
        </div>
      </div>
    </div>
    <AhaInfoBox
      variant="warning"
      v-if="!isContrastEnough"
    >
      {{
        $i18nPresenter.t('For readability, make sure there is a clear contrast between text colour and base colour.')
      }}
    </AhaInfoBox>
    <div class="divider bg-secondary-20 h-px"></div>
    <div class="gap-2 mb-12">
      <div
        class="font-bold mb-2"
      >
        {{ $i18nPresenter.t('Visualisation colours') }}
        <span
          name="presentation-color-palette"
          class="tooltip-grey"
        >
          <i class="fas fa-question-circle " />
        </span>
        <aha-toggle-tip
          to="presentation-color-palette"
          :text="visualisationTooltipContent"
        >
        </aha-toggle-tip>
      </div>
      <aha-antd-select-multi-color
        class="w-full h-10"
        :options="presetColorPalettes"
        :value="colorPalletteSelect"
        :change="handleUpdateColorPalette"
      />
    </div>
    <aha-antd-button variant="secondary" @click="applyOtherSlide" size="lg">
      {{ $i18nPresenter.t('Apply to other slides') }}
    </aha-antd-button>
    <aha-antd-button
      variant="tertiary"
      @click="resetBackgroundConfig"
      size="lg"
    >
      {{ $i18nPresenter.t('Reset to default') }}
    </aha-antd-button>

    <div v-if="isUploadImageModalShowing">
      <FeatureAIAutoPrefillImageSearchTabs
        v-if="canUseAutoPrefillImageSearchTabs"
        :type="'slideBackgroundImage'"
        @hideModal="hideModalUploadImage"
        @callback="handleUploadFileFinished"
        :updateGifInBackground="handleUpdateGifInBackground"
        @selectExistImage="selectExistImage"
      />
      <UploadImageV2
        v-else
        :type="'slideBackgroundImage'"
        @hideModal="hideModalUploadImage"
        @callback="handleUploadFileFinished"
        :updateGifInBackground="handleUpdateGifInBackground"
        @selectExistImage="selectExistImage"
      />
    </div>

    <CropImageV2
      :aspect-ratio="DEFAULT_IMAGE_RATIOS.BACKGROUND_IMAGE"
      :cropper-data="cropperData"
      v-if="isCropImageModalShowing"
      :image-url="slideBackgroundImageUrl"
      @on-cancel="handleCancelCrop"
      @hideModal="hideCropModal"
      :updateInBackground="handleUpdateInBackground"
    />

    <aha-modal
      :data="dataConfirm"
      :title="dataConfirm.title"
      v-if="dataConfirm.open"
      @hide="handleModalConfirmApply({ ok: false })"
    >
      {{ dataConfirm.description }}

      <template #action>
        <aha-button
          variant="primary"
          @click="handleModalConfirmApply({ ok: true })"
        >
          {{ $i18nPresenter.t('OK') }}
        </aha-button>
        <aha-link
          has-margin-left
          @click="handleModalConfirmApply({ ok: false })"
        >
          <span class="font-bold">{{ $i18nPresenter.t('Cancel') }}</span>
        </aha-link>
      </template>
    </aha-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import debounce from 'debounce'

import DesignEditPanel from '@/components/presentations/types/DesignEditPanel.vue'
import ColorPickerWrap from './ColorPickerWrap.vue'
import SelectFontWrap from './SelectFontWrap.vue'
import UpgradeIcon from '@/components-v2/my-plan/UpgradeIcon.vue'
import ColorButton from './ColorButton.vue'

export default {
  name: 'CustomizePanel',
  components: {
    ColorPickerWrap,
    SelectFontWrap,
    UpgradeIcon,
    ColorButton,
  },
  extends: DesignEditPanel,
  data() {
    return {
      colorPalletteSelect: '',
    }
  },
  computed: {
    ...mapGetters('presetModule', ['presetColorPalettes']),
    ...mapGetters('presentationAttributeModule', ['presentationAttributeColorPalette']),
    ...mapGetters('featureToggles', ['canUseNewColors']),
    isWhiteText() {
      return this.selectedSlide?.textColour?.toLowerCase() === '#ffffff'
    },
    isBlackText() {
      return this.selectedSlide?.textColour?.toLowerCase() === '#313131'
    },
    visualisationTooltipContent() {
      return this.$i18nPresenter.t('Visualisation colours apply to all charts in quiz, poll, and scale slides, and to audience response displays in word cloud, open ended, and brainstorm slides')
    },
    getSlideVisibility() {
      return this.selectedSlide.visibility > 0 ? this.selectedSlide.visibility : 0
    },
  },
  created() {
    this.colorPalletteSelect = this.presentationAttributeColorPalette.id
    const colorPaletteIds = {
      default: 'default',
      branding2025: 'branding-2025',
    }
    if (!this.colorPalletteSelect) {
      this.colorPalletteSelect = this.canUseNewColors ? colorPaletteIds.branding2025 : colorPaletteIds.default
    }
    this.debouncedUpdateVisibility = debounce(async () => {
      await this.updateVisibility()
    }, 1000)
  },
  methods: {
    ...mapActions(['updatePresentationByIdAction', 'updateSlideActiveAction', 'updatePresentation']),
    ...mapActions('presentationAttributeModule', ['updatePresentationAttributeAction']),
    ...mapActions('history', ['setSuppressHistoryAction']),
    updateSelectSlideVisibility(value) {
      this.updateSlideActiveAction({
        visibility: value,
      })
      this.debouncedUpdateVisibility()
    },
    handleUpdateColorPalette(value) {
      this.colorPalletteSelect = value
      const item = this.presetColorPalettes.find((palette) => palette.id === value)
      this.selectColorPalette(item)
    },
    async selectColorPalette(item) {
      this.setSuppressHistoryAction(true)
      await this.updatePresentationAttributeAction({
        presentationId: this.presentation.id,
        payload: {
          attributeName: 'colorPalette',
          attributeValue: item,
        },
      })
      const slideActive = JSON.parse(JSON.stringify(this.slideActive))
      this.updateSlideActiveAction({ type: 'freeStyle' })
      await this.$nextTick()
      this.updateSlideActiveAction({
        type: slideActive.type,
      })
      this.setSuppressHistoryAction(false)
    },
    previewSelectTextColour: debounce(async function (color) {
      await this.selectTextColour(color)
    }, 300),
    handleSetFontFamily(font) {
      this.updatePresentation({
        fontFamily: font,
      })
      this.setFontFamily(font)
    },
  },
}
</script>

<style lang="scss" scoped>
.customize-panel {
  padding: 16px;
  margin-top: 56px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.upgrade-arrow {
  width: 0;
  height: 0;
  border-width: 0 8px 8px 8px;
  border-color: transparent transparent rgba(40, 46, 62, 1) transparent;
  border-style: solid;
  position: absolute;
  z-index: 1;
  top: 1.6rem;
}
.tooltip-grey {
  color: #B5B5B5;
}
</style>
